<!--
 * @Author: xuchen
 * @Date: 2020-03-10 14:31:38
 * @Desc: 视频弹窗
 -->
<template>
  <div v-if="visible" class="video-dialog-container">
    <div class="video-content-wrap">
      <div class="video-content-container flex flex-column">
        <div class="video-content-tit flex items-center justify-between">
          <span class="dialog-tit">企业视频详情</span>
          <i class="h-icon-close close-icon" @click="handleCloseDialog"></i>
        </div>
        <div class="flex flex-1">
          <div class="video-container">
            <Pre-view-ocx
              ref="ocxVideoRef"
              :params="videoParams"
              :toolbar="true"
              @handleSend="handleSendWarn"
            />
          </div>
          <div class="info-container">
            <el-scrollbar wrap-class="info-scroll-container">
              <div v-ellipsis class="company-name">{{ enterpriseInfo.enterpriseName }}</div>
              <enterprise-info :data-provider="enterpriseInfo" />
              <monitor-point
                :selected-monitor-keys="selectedMonitorKeys"
                :data-provider="monitorPoints"
                @handleStartVideo="handleChangeVideo"
              />
            </el-scrollbar>
          </div>
        </div>
        <div class="arrow-switch arrow-switch-left" @click="handleMoveLeft">
          <i class="h-icon-angle_left"></i>
        </div>
        <div class="arrow-switch arrow-switch-right" @click="handleMoveRight">
          <i class="h-icon-angle_right"></i>
        </div>
      </div>
      <div class="bottom-container">
        <swiper-platform
          ref="swiperPlatformRef"
          :row-enterprise-num="rowEnterpriseNum"
          :default-selected-enterp-key="defaultSelectedEnterpKey"
          :selected-enterprise-key="selectedEnterpriseKey"
          :data-provider="enterpriseList"
          @selectEnterprise="handleSelectEnterp"
          @getMoreData="getMoreEnterpriseData"
        />
      </div>
    </div>
    <warning-push-dialog
      :dialog-visible.sync="dialogVisible"
      :capture-image="captureImage"
      @alarmPush="alarmPush"
    />
  </div>
</template>

<script>
import EnterpriseInfo from '@/components/EnterpriseVideoInspection/EnterpriseInfo'
import MonitorPoint from '@/components/EnterpriseVideoInspection/MonitorPoint'
import SwiperPlatform from '@/components/EnterpriseVideoInspection/SwiperPlatform'
import PreViewOcx from '@/components/VideoOcx/PreViewOcx.vue'
import WarningPushDialog from '@/components/EnterpriseVideoInspection/WarningPushDialog.vue'
import http from '@/api/enterpriseVideoInspection'
import { _debounce } from '@/utils/commonUtil'

export default {
  name: 'VideoDialog',
  components: {
    EnterpriseInfo,
    MonitorPoint,
    SwiperPlatform,
    PreViewOcx,
    WarningPushDialog
  },
  props: {
    // 是否显示弹窗
    visible: {
      type: Boolean,
      default: false
    },

    // 列表中选择的企业主键
    defaultSelectedEnterpKey: {
      type: [String, Number],
      default: ''
    },

    // 企业列表
    enterpriseList: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      rowEnterpriseNum: 7, // 一行展示7个企业
      enterpriseInfo: {}, // 企业基本信息
      selectedEnterpriseKey: '', // 选择的企业主键
      selectedMonitorKeys: [], // 选择的监控点主键
      monitorPoints: [], // 监测点
      monitorParams: {}, // 监控点萤石参数
      videoParams: {}, // 预览视频的播放参数
      curPushPoint: '', // 正在预警推送处理的监控点
      dialogVisible: false,
      captureImage: ''
    }
  },
  watch: {
    /**
     * @author xuchen
     * @date 2020-03-11 14:15:07
     * @desc 获取初始化企业详情来源
     */
    defaultSelectedEnterpKey(val) {
      this.selectedEnterpriseKey = val
      this.getAllDetail(val)
    },

    /**
     * @author xuchen
     * @date 2020-03-17 11:31:54
     * @desc 弹窗显示隐藏
     */
    visible(val) {
      // 关闭弹窗
      if (!val) {
        this.selectedMonitorKeys = []
        this.$refs.ocxVideoRef.destroyed()
      }
    }
  },
  methods: {
    /**
     * @author xuchen
     * @date 2020-03-17 19:52:42
     * @desc 获取所有的详情数据
     */
    async getAllDetail(key) {
      if (!key) return
      await this.getMonitorPoints(key)
      // this.monitorPoints[0] && this.handleChangeVideo(this.monitorPoints[0].monitorCode)
      this.getEnterpriseInfo(key)
    },

    getAllDetailDelay: _debounce(function(key) {
      this.getAllDetail(key)
    }),

    /**
     * @author xuchen
     * @date 2020-03-11 14:01:42
     * @desc 关闭弹窗
     */
    handleCloseDialog() {
      this.$emit('update:visible', false)
    },

    /**
     * @author xuchen
     * @date 2020-03-16 15:04:48
     * @desc 切换视频监控点
     */
    async handleChangeVideo(key) {
      const payload = {
        monitorCode: key,
        projectCode: this.$store.state.projectCode
      }
      const { data } = await http.getVideoParams(payload)
      this.monitorParams[key] = data
      const matchPoint = this.monitorPoints.find(point => point.monitorCode === key)
      this.videoParams = { ...data, cameraIndexCode: key, cameraName: matchPoint.monitorName }
      // 更新监控点封面请求
      http.updateCover(payload)
    },

    /**
     * @author xuchen
     * @date 2020-03-16 16:36:03
     * @desc 选择企业
     */
    handleSelectEnterp(key) {
      this.selectedEnterpriseKey = key
      this.$refs.ocxVideoRef.closeVideo()
      this.getAllDetail(key)
    },

    /**
     * @author xuchen
     * @date 2020-03-16 17:47:23
     * @desc 视频上的操作回调
     */
    handleSendWarn({ indexCode, type, pic }) {
      if (type === 'AlarmPush') {
        this.curPushPoint = indexCode
        this.dialogVisible = true
        this.captureImage = pic
      } else if (type === 'StopPreview') {
        this.selectedMonitorKeys.splice(this.selectedMonitorKeys.indexOf(indexCode), 1)
      } else if (type === 'StartPreview') {
        this.selectedMonitorKeys.push(indexCode)
      }
    },
    /**
     * @Author: maogangfeng
     * @Date: 2020-03-23 18:48:03
     * @Desc: 预警推送
     */

    async alarmPush(params) {
      const { curPushPoint, monitorParams, selectedEnterpriseKey } = this
      const devParams = monitorParams[curPushPoint]
      await http.alarmPush({
        channelNo: devParams.channelNo,
        deviceSerial: devParams.serialNumber,
        enterpriseCode: selectedEnterpriseKey,
        projectCode: this.$store.state.projectCode,
        monitorCode: curPushPoint,
        ...params
      })
      this.$message.success('推送成功！')
      this.dialogVisible = false
    },

    /**
     * @author xuchen
     * @date 2020-03-17 10:21:18
     * @desc 获取企业信息
     */
    async getEnterpriseInfo(key) {
      const { data } = await http.getEnterpriseInfo({ enterpriseCode: key })
      this.enterpriseInfo = data
    },

    /**
     * @author xuchen
     * @date 2020-03-17 10:22:22
     * @desc 获取监控点
     */
    async getMonitorPoints(key) {
      const { data } = await http.getMonitorPoints({ enterpriseCode: key })
      this.monitorPoints = data
    },

    /**
     * @author xuchen
     * @date 2020-03-17 19:14:16
     * @desc 获取更多企业数据
     */
    getMoreEnterpriseData() {
      this.$emit('getMoreEnterpriseData')
    },

    /**
     * @author xuchen
     * @date 2020-03-17 20:20:29
     * @desc 左移动一个企业
     */
    handleMoveLeft() {
      const matchIdx = this.enterpriseList.findIndex(
        i => i.enterpriseCode === this.selectedEnterpriseKey
      )

      // 第一个企业
      if (matchIdx <= 0) return
      this.selectedEnterpriseKey = this.enterpriseList[matchIdx - 1].enterpriseCode

      // 选择了当前一排的第一个企业
      if ((matchIdx + 1) % this.rowEnterpriseNum === 1) {
        this.$refs.swiperPlatformRef.shouldMoveLeft(matchIdx) &&
          this.$refs.swiperPlatformRef.handleMoveLeft()
      }
      this.$refs.ocxVideoRef.closeVideo()
      this.getAllDetailDelay(this.selectedEnterpriseKey)
    },

    /**
     * @author xuchen
     * @date 2020-03-17 20:21:14
     * @desc 右移动一个企业
     */
    handleMoveRight() {
      const matchIdx = this.enterpriseList.findIndex(
        i => i.enterpriseCode === this.selectedEnterpriseKey
      )

      // 最后一个企业
      if (matchIdx >= this.enterpriseList.length - 1) return
      this.selectedEnterpriseKey = this.enterpriseList[matchIdx + 1].enterpriseCode

      // 选择了当前一排的最后一个企业
      if ((matchIdx + 1) % this.rowEnterpriseNum === 0) {
        this.$refs.swiperPlatformRef.shouldMoveRight(matchIdx + 1) &&
          this.$refs.swiperPlatformRef.handleMoveRight()
        // this.$refs.swiperPlatformRef.handleMoveRight()
      }
      this.$refs.ocxVideoRef.closeVideo()
      this.getAllDetailDelay(this.selectedEnterpriseKey)
    }
  }
}
</script>

<style lang="scss" scoped>
.video-dialog-container {
  position: fixed;
  left: 0;
  top: 0;
  z-index: 999;
  width: 100%;
  height: 100%;
  background: rgba(26, 26, 26, 0.9);
  overflow-y: auto;
  .dialog-tit {
    color: rgba(0, 0, 0, 0.9);
  }
  .video-content-wrap {
    position: relative;
    top: 50%;
    transform: translateY(-50%);
  }
  .video-content-container {
    position: relative;
    width: 1022px;
    height: 600px;
    margin: 0 auto 27px;
    background-color: #fff;
    .company-name {
      height: 40px;
      line-height: 40px;
      padding-left: 8px;
      font-size: 18px;
      color: rgba(0, 0, 0, 0.9);
    }
    .video-content-tit {
      height: 40px;
      padding: 0 8px 0 15px;
      border-bottom: 1px solid #dad9d9;
    }
    .video-container {
      width: 740px;
      height: 100%;
    }
    .info-container {
      width: 282px;
      height: 100%;
    }
    ::v-deep .info-scroll-container {
      height: 560px;
      overflow-x: visible;
    }
    .close-icon {
      font-size: 24px;
      cursor: pointer;
    }
    .arrow-switch {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      font-size: 72px;
      color: rgba(255, 255, 255, 0.7);
      cursor: pointer;
      &:hover {
        color: rgba(255, 255, 255, 0.8);
      }
      &:active {
        color: rgba(255, 255, 255, 0.6);
      }
    }
    .arrow-switch-left {
      left: -100px;
    }
    .arrow-switch-right {
      right: -100px;
    }
  }
  .bottom-container {
    position: relative;
    margin: 0 auto;
  }
}
</style>
